// @flow
import React, { useEffect } from 'react';

import {
  AWS,
  AZURE,
  GCP,
  CLUSTER_FOLLOWER_FETCH_BACKEND_NAME,
  CLUSTER_NETWORKING_TYPE_BACKEND_NAME,
  CLUSTER_PROVIDER_BACKEND_NAME,
  CLUSTER_REGION_BACKEND_NAME,
  CLUSTER_TYPE_BACKEND_NAME,
  PEERED_VPC,
  DEDICATED_CLUSTER_TYPE,
  CLUSTER_DETAILS_FORM,
  CLUSTER_KAFKA_FORM,
  CLUSTER_KSQLDB_FORM,
  CLUSTER_CONNECTORS_FORM,
  CLUSTER_CLUSTER_LINKING_FORM,
  FLINK_POOL_DETAILS_FORM,
  FLINK_POOL_MONTHLY_INPUTS_FORM,
  ENTERPRISE_CLUSTER_TYPE,
  FREIGHT_CLUSTER_TYPE,
  IS_CONNECT_CROSS_CLOUD_REGION_LIVE,
  NATIVE_INFERENCE_POOL_DETAILS_FORM,
  NATIVE_INFERENCE_POOL_CCU_MONTHLY_INPUTS_FORM,
  NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_FORM,
} from '../../../constants';
import { getDropdownOptions } from '../../stream-governance/utils';
import {
  CLUSTER_DETAILS_CONFIG_MAP,
  CLUSTER_DETAILS_FORM_NAMES_TO_FIELDS_MAP,
} from '../../../configuration/cluster-details';
import {
  extractRelevantFieldValuesFromForm,
  getArrayElemsExcludingGivenElem,
  getFormattedRegionsUniverse,
  getIfCustomerViewIsEnabled,
  isFormEditedBasedOnCurrentValues,
  resetFormValuesToInitialValues,
} from '../../../common-utils/utils';
import { FLINK_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP } from '../../../configuration/flink-pool-details';
import { NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP } from '../../../configuration/native-inference-pool-details';

/**
 * @param {Array} dataUniverse - the cluster_params provider universe
 * @param {Object} values - current formik values to get Parent field values i.e., cluster type values
 */
export const getClusterProviderDropdownOptions = (dataUniverse, values) =>
  getDropdownOptions(
    dataUniverse,
    values,
    CLUSTER_DETAILS_CONFIG_MAP.get(CLUSTER_PROVIDER_BACKEND_NAME),
    false,
    true,
    false,
    false,
    [AWS, AZURE, GCP]
  );

/**
 * Builds the region dropdown options scoped to the cloud provider's value.
 * Used by both the cluster region and the connector destination region
 * Regions are sorted by their display label.
 * @param {Array} regionDetails - the region details data universe
 * @param {string} provider - the cloud provider which is used to filter the region values
 */
export const getRegionDropdownOptions = (regionDetails = [], provider) => {
  // Empty until a provider is selected, else every region would show.
  if (!provider) {
    return [];
  }

  const formattedRegions = getFormattedRegionsUniverse(regionDetails);

  return getDropdownOptions(
    formattedRegions,
    { [CLUSTER_PROVIDER_BACKEND_NAME]: provider },
    CLUSTER_DETAILS_CONFIG_MAP.get(CLUSTER_REGION_BACKEND_NAME),
    false,
    true,
    false,
    false,
    [],
    true
  );
};

export const handleDependedClusterInputFields = (
  columnConfig,
  dataUniverse,
  values,
  setFieldValue,
  setFieldTouched
) => {
  // todo::SP Ensure that SP using the 2.0 config is okay - otherwise separate out the configs..
  const dependentFieldsArr = columnConfig?.dependentFields ?? [];
  for (const dep of dependentFieldsArr) {
    const options = getDropdownOptions(dataUniverse, values, CLUSTER_DETAILS_CONFIG_MAP.get(dep));

    if (!options.map((x) => x.name).includes(values[dep])) {
      setFieldValue(dep, null);
      setFieldTouched(dep, true);
    }
    if (options.length === 1) {
      setFieldValue(dep, options[0].name);
    }
  }
};

export const getRelevantValues = (columnConfig, values) => {
  const valuesToUse = {};
  const parentFieldsInGroup = columnConfig?.parentFieldsInGroup ?? [];
  const dependentFieldsArr = columnConfig?.dependentFields ?? [];

  for (const field of parentFieldsInGroup) {
    valuesToUse[field] = values[field];
  }

  for (const field of dependentFieldsArr) {
    valuesToUse[field] = values[field];
  }
  return valuesToUse;
};

export const useClusterInputsDependencies = (
  columnConfig,
  dataUniverse,
  values,
  setFieldValue,
  setFieldTouched
) => {
  // todo:: This is causing slowness as the Cluster Type/Cluster Availability/Cluster Networking etc. get
  //  re-rendered even when Partitions change
  // We need to do something like the below but it is creating infinite loops somewhere
  // Note: This is done here because if we just use "values", then these fields will rerender even when other
  // unrelated fields like Partitions also change
  // const valuesToUse = getRelevantValues(clusterAvailabilityConfig, values);

  useEffect(() => {
    handleDependedClusterInputFields(
      columnConfig,
      dataUniverse,
      values,
      setFieldValue,
      setFieldTouched
    );
  }, [
    values,
    dataUniverse,
    setFieldValue,
    setFieldTouched,
    columnConfig.dependentFields,
    columnConfig,
  ]);
};

export const disableFollowerFetchOption = (setFieldValue) => {
  setFieldValue(CLUSTER_FOLLOWER_FETCH_BACKEND_NAME, false);
};

export const shouldShowFFOption = (values, estimate, isUserAdmin) => {
  const isValidDedicatedClusterCombination =
    values[CLUSTER_PROVIDER_BACKEND_NAME] === AWS &&
    values[CLUSTER_TYPE_BACKEND_NAME] === DEDICATED_CLUSTER_TYPE &&
    values[CLUSTER_NETWORKING_TYPE_BACKEND_NAME] === PEERED_VPC;

  const isValidFreightClusterCombination =
    values[CLUSTER_TYPE_BACKEND_NAME] === FREIGHT_CLUSTER_TYPE;

  const shouldShowFFBasedOnClusterDetails =
    isValidDedicatedClusterCombination || isValidFreightClusterCombination;

  return (
    shouldShowFFBasedOnClusterDetails &&
    !getIfCustomerViewIsEnabled(estimate?.inputs) &&
    isUserAdmin
  );
};

export const fetchFormValues = (values, formName, formNameToFieldsMap) => {
  const fieldsInForm = formNameToFieldsMap.get(formName);
  return extractRelevantFieldValuesFromForm(fieldsInForm, values);
};

export const resetValues = (values, initialValues, resetForm, formName, formNameToFieldsMap) => {
  const fieldsInForm = formNameToFieldsMap.get(formName);
  resetFormValuesToInitialValues(values, fieldsInForm, initialValues, resetForm);
};

export const isFormEdited = (values, initialValues, formName, formNameToFieldsMap) => {
  const fieldsInForm = formNameToFieldsMap.get(formName);
  return isFormEditedBasedOnCurrentValues(values, initialValues, fieldsInForm);
};

export const isAnyOfTheGivenFormEdited = (
  values,
  initialValues,
  formNames,
  formNameToFieldsMap
) => {
  let toRet = false;
  for (const formName of formNames) {
    if (isFormEdited(values, initialValues, formName, formNameToFieldsMap)) {
      toRet = true;
      break;
    }
  }
  return toRet;
};

export const isAnyOfTheClusterRelatedFormsEdited = (values, initialValues) => {
  return isAnyOfTheGivenFormEdited(
    values,
    initialValues,
    [
      CLUSTER_DETAILS_FORM,
      CLUSTER_KAFKA_FORM,
      CLUSTER_KSQLDB_FORM,
      CLUSTER_CONNECTORS_FORM,
      CLUSTER_CLUSTER_LINKING_FORM,
    ],
    CLUSTER_DETAILS_FORM_NAMES_TO_FIELDS_MAP
  );
};

export const isAnyOfTheFlinkPoolRelatedFormsEdited = (values, initialValues) => {
  return isAnyOfTheGivenFormEdited(
    values,
    initialValues,
    [FLINK_POOL_DETAILS_FORM, FLINK_POOL_MONTHLY_INPUTS_FORM],
    FLINK_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP
  );
};

export const isAnythingEditedOnNativeInferencePoolPageBesidesTheGivenForm = (
  values,
  initialValues,
  currentFormName
) => {
  const allNativeInferencePoolForms = [
    NATIVE_INFERENCE_POOL_DETAILS_FORM,
    NATIVE_INFERENCE_POOL_CCU_MONTHLY_INPUTS_FORM,
    NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_FORM,
  ];

  return isAnyOfTheGivenFormEdited(
    values,
    initialValues,
    getArrayElemsExcludingGivenElem(allNativeInferencePoolForms, currentFormName),
    NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP
  );
};

export const isEnterpriseCluster = (values) => {
  return values[CLUSTER_TYPE_BACKEND_NAME] === ENTERPRISE_CLUSTER_TYPE;
};

const clusterProviderRegionChangeConfirmationBody = (
  <>
    <p>
      Changing the cluster&apos;s cloud provider or region will re-evaluate throughput charges for
      all Connect Public sink connectors. Each connector will be reclassified as Same Region or
      Cross Region / Cross Cloud based on its destination provider and region. As a result,
      connector spend and your total commit may change.
    </p>
    <p>
      If a connector&apos;s destination region is no longer valid, we&apos;ll update it
      automatically. If a region is now required but missing, we&apos;ll set it to the new cluster
      region. If a destination region is no longer needed, we&apos;ll remove it. You can update
      these settings later if needed.
    </p>
    <p>
      To keep your changes, click<strong> Yes, proceed</strong>. To keep editing, click
      <strong> Cancel</strong>.
    </p>
  </>
);

export const getClusterDetailsConfirmationConfigsForSave = (values, initialValues) => {
  const confirmationConfigs = [];

  const hasProviderOrRegionChanged =
    values[CLUSTER_PROVIDER_BACKEND_NAME] !== initialValues[CLUSTER_PROVIDER_BACKEND_NAME] ||
    values[CLUSTER_REGION_BACKEND_NAME] !== initialValues[CLUSTER_REGION_BACKEND_NAME];

  // Changing the cluster provider/region re-prices Connect Public sink connectors, so confirm
  // before saving.
  if (IS_CONNECT_CROSS_CLOUD_REGION_LIVE && hasProviderOrRegionChanged) {
    confirmationConfigs.push({
      header: 'Change in Cluster Provider / Region',
      body: clusterProviderRegionChangeConfirmationBody,
      okButtonText: 'Yes, proceed',
      cancelButtonText: 'Cancel',
    });
  }

  return confirmationConfigs;
};
